import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useNavigate } from 'react-router';

import { Endpoints } from '@/config/endpoints';
import { LanguageRoute, PersonsRoute, VerifyAdminRoute } from '@/config/router/routes';
import { appRepository } from '@/data/appRepositoryImpl';
import { tabletRepository } from '@/data/tabletRepositoryImpl';
import { TabletAuthOutcome, TabletSessionManager } from '@/data/tabletSessionManager';
import type { PublicTablet } from '@/domain/models/publicTablet';
import type { VerifyAdminType } from '@/presentation/pages/verifyAdmin/verifyAdminType';
import { appStateStore } from '@/presentation/stores/appStateStore';
import { EasyLoading } from '@/presentation/widgets/loading/loadingOverlay';
import { useTextController } from '@/presentation/widgets/PrimaryInputField';
import { SnackbarManager } from '@/presentation/widgets/snackbar/snackbarManager';
import { closeKeyboard } from '@/utils/appHelpers';
import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';
import { stackTraceOf } from '@/utils/exceptions/appException';
import { AppFailure, FailureKind, FailureOp } from '@/utils/exceptions/appFailure';
import { ServerAddress } from '@/utils/serverAddress';
import { StorageHelper } from '@/utils/storageHelper';

/**
 * Состояние `_BaseUrlPageState` из
 * `lib/src/presentation/pages/base_url_page/base_url_page.dart`.
 *
 * То, что участвует в отрисовке, лежит в `useState`; то, что Dart читал
 * посреди асинхронной операции (`_handledText`, `_scheme`, `_connecting`,
 * `_loadingTablets`, `mounted`), — в `useRef`. Через замыкание React отдал
 * бы значения того кадра, в котором операция началась, и проверка «пока
 * грузили, адрес могли поменять» перестала бы работать.
 */

/** Пароль отвергнут сервером — `AppFailure(kind: badRequest, statusCode: 403)`. */
const HTTP_FORBIDDEN = 403;

/** Исчерпан лимит попыток входа. */
const HTTP_TOO_MANY_REQUESTS = 429;

/**
 * Эндпоинта списка на сервере нет: 401 или 404 (так сейчас в проде),
 * либо по адресу отвечает не API.
 */
const SERVER_WITHOUT_LIST_STATUS_CODES: readonly number[] = [401, 403, 404, 405];

function isServerWithoutList(failure: AppFailure): boolean {
  switch (failure.kind) {
    case FailureKind.badContract:
      return true;
    case FailureKind.badRequest:
      return (
        failure.statusCode !== null &&
        SERVER_WITHOUT_LIST_STATUS_CODES.includes(failure.statusCode)
      );
    default:
      return false;
  }
}

/**
 * Серверы для выпадающего списка: предустановленные + запомненные
 * после успешной проверки. Дубликаты убраны, пресеты первыми.
 */
function buildKnownServers(): readonly string[] {
  const saved = StorageHelper.instance.knownServers;
  return [
    ...Endpoints.knownServerPresets,
    ...saved.filter((s) => !Endpoints.knownServerPresets.includes(s)),
  ];
}

/** `_currentFirst`: текущий планшет встаёт в начало списка. */
function currentFirst(
  tablets: readonly PublicTablet[],
  isCurrent: (tablet: PublicTablet) => boolean,
): readonly PublicTablet[] {
  const index = tablets.findIndex(isCurrent);
  if (index <= 0) return tablets;
  return [tablets[index], ...tablets.slice(0, index), ...tablets.slice(index + 1)];
}

/** Всё, что в Dart считалось один раз в `initState`. */
interface BaseUrlInitial {
  /**
   * Экран открыт первым после выхода по PIN
   * (см. `StorageHelper.askBaseUrlAfterExit`).
   */
  readonly afterExit: boolean;
  readonly address: ServerAddress;
  readonly handledText: string;

  /**
   * Сервер и планшет, к которым подключены сейчас. Пометка «текущий»
   * ставится только в списке этого же сервера: slug на другом сервере —
   * другой планшет.
   */
  readonly currentRoot: string | null;
  readonly currentSlug: string | null;
}

function computeInitial(): BaseUrlInitial {
  const storage = StorageHelper.instance;
  const afterExit = storage.askBaseUrlAfterExit;
  // Текущий адрес подставляем только после выхода по PIN — это администратор.
  // Сюда же попадает посетитель, когда сервер недоступен: ему внутренний
  // адрес видеть незачем.
  const initial = ServerAddress.parse(
    afterExit
      ? (storage.baseUrl ?? '')
      : // `kDebugMode` в вебе — это режим сборки Vite.
        import.meta.env.DEV
        ? Endpoints.testBaseUrl
        : '',
  );
  if (afterExit) {
    // В поле только домен: список планшетов загрузится сразу.
    return {
      afterExit,
      address: initial,
      handledText: initial.host,
      currentRoot: initial.root,
      currentSlug: initial.path,
    };
  }
  return {
    afterExit,
    address: initial,
    handledText: initial.hasPath ? `${initial.host}/${initial.path}` : initial.host,
    currentRoot: null,
    currentSlug: null,
  };
}

/** Состояние и действия экрана — то, что в Dart было полями и методами State. */
export interface BaseUrlController {
  /** `_controller.text` */
  readonly text: string;
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly scheme: string;
  readonly serversExpanded: boolean;
  readonly passwordTablet: PublicTablet | null;
  /** `_passwordController.text` */
  readonly passwordText: string;
  readonly tablets: readonly PublicTablet[] | null;
  readonly serverWithoutList: boolean;
  /** `_knownServers` */
  readonly knownServers: readonly string[];
  /** `_isCurrent` */
  readonly isCurrent: (tablet: PublicTablet) => boolean;
  /** `_onTextChanged` */
  readonly handleTextChanged: (text: string) => void;
  readonly handlePasswordChanged: (text: string) => void;
  /** `_toggleScheme` */
  readonly toggleScheme: () => void;
  readonly toggleServers: () => void;
  /** `_selectKnownServer` */
  readonly selectKnownServer: (root: string) => void;
  /** `_removeKnownServer` */
  readonly removeKnownServer: (root: string) => Promise<void>;
  /** `_submit` */
  readonly submit: () => Promise<void>;
  /** `_selectTablet` */
  readonly selectTablet: (tablet: PublicTablet) => void;
  /** `_askTabletPassword` */
  readonly askTabletPassword: (tablet: PublicTablet) => void;
  /** `_retryConnect` */
  readonly retryConnect: () => void;
  /** `_connectWithoutAuth` */
  readonly connectWithoutAuth: () => Promise<void>;
  /** `_cancel` */
  readonly cancel: () => Promise<void>;
}

export function useBaseUrlController(
  verifyAdminType: VerifyAdminType,
): BaseUrlController {
  const navigate = useNavigate();

  // `late final` из `initState`: считается один раз за жизнь экрана.
  const [initial] = useState(computeInitial);

  const controller = useTextController(initial.handledText);
  const password = useTextController();

  const [scheme, setSchemeState] = useState(initial.address.scheme);
  const [serversExpanded, setServersExpanded] = useState(false);
  const [passwordTablet, setPasswordTablet] = useState<PublicTablet | null>(null);
  const [listServer, setListServer] = useState<ServerAddress | null>(null);
  const [tablets, setTablets] = useState<readonly PublicTablet[] | null>(null);
  const [serverWithoutList, setServerWithoutList] = useState(false);

  /**
   * В Dart `_knownServers` — геттер, он перечитывал хранилище на каждой
   * сборке. Здесь список лежит в состоянии и обновляется там же, где Dart
   * звал `setState` после записи в хранилище.
   */
  const [knownServers, setKnownServers] = useState(buildKnownServers);

  /** `_handledText`: слушатель срабатывал и на движение курсора. */
  const handledTextRef = useRef(initial.handledText);

  /** `_scheme` для кода после `await`: в замыкании он был бы устаревшим. */
  const schemeRef = useRef(scheme);

  /** `_schemeChosenByHand`: догадка по тексту больше не вмешивается. */
  const schemeChosenByHandRef = useRef(false);

  const connectingRef = useRef(false);
  const loadingTabletsRef = useRef(false);

  /** `mounted`: по нему решается, переходить ли на другой экран. */
  const mountedRef = useRef(true);

  /** `addPostFrameCallback` из `_onTextChanged`. */
  const stripSchemeRef = useRef(false);

  /** `TextSelection.collapsed(offset: text.length)` из `_replaceText`. */
  const caretToEndRef = useRef(false);

  const setScheme = (next: string): void => {
    schemeRef.current = next;
    setSchemeState(next);
  };

  /** `ServerAddress get _address` — по живым значениям, а не по снимку кадра. */
  const currentAddress = (): ServerAddress =>
    ServerAddress.parse(handledTextRef.current, schemeRef.current);

  /**
   * `_isCurrent`, но с явным сервером: в Dart `_currentFirst` вызывался
   * внутри `setState` уже после `_listServer = server` и сравнивал
   * с новым сервером, а не с прежним.
   */
  const isCurrentOn =
    (server: ServerAddress | null) =>
    (tablet: PublicTablet): boolean =>
      (server?.root ?? null) === initial.currentRoot &&
      tablet.slug === initial.currentSlug;

  const isCurrent = isCurrentOn(listServer);

  /** Список относится к конкретному серверу — при смене адреса он неверен. */
  const resetList = (): void => {
    setTablets(null);
    setListServer(null);
    setServerWithoutList(false);
    setPasswordTablet(null);
    password.clear();
  };

  /** Меняет текст поля, не сбрасывая схему и список. */
  const replaceText = (text: string): void => {
    handledTextRef.current = text;
    controller.setValue(text);
    caretToEndRef.current = true;
  };

  /** `_onTextChanged` — слушатель контроллера поля адреса. */
  const handleTextChanged = (text: string): void => {
    // Во Flutter контроллер уже держал новый текст, когда срабатывал
    // слушатель; здесь его записываем сами.
    controller.setValue(text);
    if (text === handledTextRef.current) return;
    handledTextRef.current = text;

    const explicit = ServerAddress.explicitScheme(text);
    if (text.trim().length === 0) {
      schemeChosenByHandRef.current = false;
    }
    if (explicit !== null) {
      setScheme(explicit);
      schemeChosenByHandRef.current = true;
    } else if (!schemeChosenByHandRef.current) {
      setScheme(ServerAddress.guessScheme(text));
    }
    resetList();

    if (explicit !== null) {
      // Вставили адрес целиком: схема уже в чипе, из поля её убираем.
      // После кадра, а не посреди обновления поля.
      stripSchemeRef.current = true;
    }
  };

  /**
   * Проверяет и сохраняет адрес, затем ведёт к выбору аватара. true — адрес
   * подтверждён, даже если экран успел закрыться.
   *
   * Экран языка не показывается: планшет только что выбрали, следующий шаг —
   * аватар. PIN спрашивается лишь когда у планшета нет сессии: верный PIN
   * даёт токены, без которых не будет ключей STT. У того же планшета после
   * выхода по PIN и у планшета, куда вошли по паролю, токены уже есть.
   */
  const checkBaseUrl = async (baseUrl: string): Promise<boolean> => {
    try {
      EasyLoading.dismiss();
      EasyLoading.show();
      const result = await appStateStore.getState().checkAndSetBaseUrl(baseUrl);
      if (!result || !mountedRef.current) return result;
      // Адрес подтверждён — сервер попадает в выпадающий список. Покрывает
      // и серверы без списка планшетов (ввод адреса вместе со slug'ом).
      const storage = StorageHelper.instance;
      await storage.addKnownServer(ServerAddress.parse(baseUrl).root);
      if (!mountedRef.current) return true;
      if (initial.afterExit) {
        await storage.setAskBaseUrlAfterExit(false);
        if (!mountedRef.current) return true;
      }
      // setBaseUrl внутри checkAndSetBaseUrl уже стёр креды другого планшета:
      // совпадение адреса и живой токен — сессия именно этого планшета.
      if (storage.hasTabletSession(baseUrl)) {
        PersonsRoute.go(navigate);
      } else {
        VerifyAdminRoute.go(navigate, verifyAdminType);
      }
      return true;
    } finally {
      EasyLoading.dismiss();
    }
  };

  const loadTablets = async (server: ServerAddress): Promise<void> => {
    if (loadingTabletsRef.current) return;
    loadingTabletsRef.current = true;
    try {
      EasyLoading.dismiss();
      EasyLoading.show();
      const loaded = await appRepository.fetchPublicTablets(server.root);
      // Сервер ответил списком — он настоящий, запоминаем для дропдауна.
      await StorageHelper.instance.addKnownServer(server.root);
      // Пока грузили, адрес могли поменять.
      if (!mountedRef.current || currentAddress().root !== server.root) return;
      setListServer(server);
      setTablets(currentFirst(loaded, isCurrentOn(server)));
      setServerWithoutList(false);
      setKnownServers(buildKnownServers());
      // Клавиатура закрывала почти весь список — убираем её, выбирать
      // дальше нужно касанием.
      if (loaded.length > 0) {
        closeKeyboard();
      }
    } catch (e) {
      const failure = AppFailure.fromException(FailureOp.tablets, e, stackTraceOf(e));
      if (!mountedRef.current || currentAddress().root !== server.root) return;
      if (isServerWithoutList(failure)) {
        console.debug(`[Failure] ${failure.diagnostics}`, failure.cause);
        DiagnosticsLog.instance.failure(failure);
        resetList();
        setServerWithoutList(true);
        // После выхода по PIN возвращаем в поле адрес текущего планшета —
        // подтвердить его можно одним нажатием.
        const slug = initial.currentSlug;
        if (
          server.root === initial.currentRoot &&
          slug !== null &&
          slug.length > 0 &&
          !currentAddress().hasPath
        ) {
          replaceText(`${server.host}/${slug}`);
        }
      } else {
        SnackbarManager.instance.showFailure(failure);
      }
    } finally {
      loadingTabletsRef.current = false;
      EasyLoading.dismiss();
    }
  };

  const submit = async (): Promise<void> => {
    const address = currentAddress();
    if (address.isEmpty) return;
    if (address.hasPath) {
      // Адрес вместе с планшетом — как раньше. Это же путь для серверов
      // без списка планшетов.
      await checkBaseUrl(address.url);
    } else {
      await loadTablets(address);
    }
  };

  const toggleScheme = (): void => {
    setScheme(
      schemeRef.current === ServerAddress.https
        ? ServerAddress.http
        : ServerAddress.https,
    );
    schemeChosenByHandRef.current = true;
    resetList();
  };

  const selectKnownServer = (root: string): void => {
    const parsed = ServerAddress.parse(root);
    setScheme(parsed.scheme);
    schemeChosenByHandRef.current = true;
    setServersExpanded(false);
    resetList();
    replaceText(parsed.host);
    void loadTablets(parsed);
  };

  const removeKnownServer = async (root: string): Promise<void> => {
    // Пресеты не удаляются — кнопка long-press на них и не вешается.
    await StorageHelper.instance.removeKnownServer(root);
    if (!mountedRef.current) return;
    setKnownServers(buildKnownServers());
  };

  /**
   * Подключение как раньше — в один тап, пароль никогда не блокирует.
   *
   * Перед подключением делается probe `tablets/auth` (с введённым либо
   * сохранённым для этого адреса паролем) — если вход удался, планшет
   * получит STT-ключи. Любой отказ, включая «нужен пароль», деградирует
   * в прежнее подключение без ключей; пароль вводится отдельно,
   * по long-press на планшете.
   */
  const connectTablet = async (
    server: ServerAddress,
    tablet: PublicTablet,
  ): Promise<void> => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    const url = new ServerAddress({
      scheme: server.scheme,
      host: server.host,
      path: tablet.slug,
    }).url;
    const typedPassword = password.value;
    const storage = StorageHelper.instance;
    // Пустое поле — пробуем сохранённый для этого же адреса пароль:
    // фоновая цепочка ключей продолжает работать без единого экрана.
    const saved = storage.credentialsMatch(url) ? (storage.tabletPassword ?? '') : '';
    const sentPassword = typedPassword.length > 0 ? typedPassword : saved;
    // Вход сохраняет креды ещё до проверки адреса — фоновой цепочке ключей
    // они нужны уже в ней. Адрес не подтвердился — возвращаем прежние,
    // иначе текущий планшет теряет пароль и не обновит ключ STT после 401.
    const previous = storage.tabletCredentialsSnapshot;
    let authorized = false;
    let confirmed = false;
    try {
      EasyLoading.dismiss();
      EasyLoading.show();
      const outcome = await TabletSessionManager.instance.authorize({
        serverRoot: server.root,
        slug: tablet.slug,
        password: sentPassword,
        repository: tabletRepository,
      });
      authorized = outcome === TabletAuthOutcome.success;
      if (!mountedRef.current) return;
      switch (outcome) {
        case TabletAuthOutcome.success:
        case TabletAuthOutcome.unsupported:
          // unsupported — сервер без входа планшета: работаем без ключей.
          confirmed = await checkBaseUrl(url);
          break;
        case TabletAuthOutcome.wrongPassword:
          if (typedPassword.length === 0) {
            // Пароль не вводили — подключаемся без ключей, как раньше.
            // Ключи STT придут с верным PIN (новый бекенд) либо позже —
            // по long-press на планшете с паролем.
            console.debug(
              `[BaseUrl] планшет ${tablet.slug} требует пароль — подключение ` +
                'без ключей STT',
            );
            confirmed = await checkBaseUrl(url);
          } else {
            SnackbarManager.instance.showFailure(
              new AppFailure({
                op: FailureOp.tabletAuth,
                kind: FailureKind.badRequest,
                statusCode: HTTP_FORBIDDEN,
              }),
            );
          }
          break;
        case TabletAuthOutcome.rateLimited:
          SnackbarManager.instance.showFailure(
            new AppFailure({
              op: FailureOp.tabletAuth,
              kind: FailureKind.badRequest,
              statusCode: HTTP_TOO_MANY_REQUESTS,
            }),
          );
          break;
      }
    } finally {
      if (authorized && !confirmed) {
        await storage.restoreTabletCredentials(previous);
      }
      connectingRef.current = false;
      EasyLoading.dismiss();
    }
  };

  const selectTablet = (tablet: PublicTablet): void => {
    const server = listServer;
    if (server === null) return;
    void connectTablet(server, tablet);
  };

  /**
   * Long-press по планшету — необязательный вход по паролю, чтобы получить
   * ключи STT. Обычный тап этой панели не показывает.
   */
  const askTabletPassword = (tablet: PublicTablet): void => {
    setPasswordTablet(tablet);
    password.clear();
  };

  /** Повторная попытка входа с введённым паролем. */
  const retryConnect = (): void => {
    const server = listServer;
    const tablet = passwordTablet;
    if (server === null || tablet === null) return;
    void connectTablet(server, tablet);
  };

  /**
   * «Подключиться без пароля» из панели пароля: сознательный отказ
   * от ключей STT.
   */
  const connectWithoutAuth = async (): Promise<void> => {
    const server = listServer;
    const tablet = passwordTablet;
    if (server === null || tablet === null) return;
    const url = new ServerAddress({
      scheme: server.scheme,
      host: server.host,
      path: tablet.slug,
    }).url;
    const storage = StorageHelper.instance;
    // Сохранённый пароль этого же планшета иначе подтянул бы ключи в фоне.
    // Адрес не подтвердился — отказ не состоялся, креды возвращаем.
    const previous = storage.tabletCredentialsSnapshot;
    const hadCredentials = storage.credentialsMatch(url);
    if (hadCredentials) await storage.clearTabletCredentials();
    const confirmed = await checkBaseUrl(url);
    if (hadCredentials && !confirmed) {
      await storage.restoreTabletCredentials(previous);
    }
  };

  /**
   * Крестик оставляет прежний адрес. После выхода по PIN он же снимает
   * вопрос: без сети адрес не подтвердить, и планшет не должен застрять здесь.
   */
  const cancel = async (): Promise<void> => {
    if (initial.afterExit) {
      await StorageHelper.instance.setAskBaseUrlAfterExit(false);
    }
    if (!mountedRef.current) return;
    LanguageRoute.go(navigate);
  };

  // `mounted` у State: снимается при размонтировании, ставится при монтаже.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Хвост `initState`: после выхода по PIN список планшетов грузится
  // на следующем кадре. Это именно `initState` — эффект выполняется один
  // раз, а не на каждое пересоздание `loadTablets`.
  useEffect(() => {
    if (!initial.afterExit || initial.address.isEmpty) return;
    void loadTablets(initial.address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `TextSelection.collapsed(offset: text.length)` из `_replaceText`.
  // Стоит перед эффектом ниже: тот меняет текст, и курсор должен встать
  // в конец уже нового значения, а не прежнего.
  useEffect(() => {
    if (!caretToEndRef.current) return;
    caretToEndRef.current = false;
    const input = controller.ref.current;
    if (input === null) return;
    input.setSelectionRange(input.value.length, input.value.length);
  });

  // `addPostFrameCallback` из `_onTextChanged`: схему из поля убираем
  // после кадра, а не посреди его обновления.
  useEffect(() => {
    if (!stripSchemeRef.current) return;
    stripSchemeRef.current = false;
    replaceText(ServerAddress.stripScheme(controller.value));
  });

  return {
    text: controller.value,
    inputRef: controller.ref,
    scheme,
    serversExpanded,
    passwordTablet,
    passwordText: password.value,
    tablets,
    serverWithoutList,
    knownServers,
    isCurrent,
    handleTextChanged,
    handlePasswordChanged: password.setValue,
    toggleScheme,
    toggleServers: () => setServersExpanded((expanded) => !expanded),
    selectKnownServer,
    removeKnownServer,
    submit,
    selectTablet,
    askTabletPassword,
    retryConnect,
    connectWithoutAuth,
    cancel,
  };
}
